import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { PushPin, Search, Category } from '@mui/icons-material'
import { Box, Button, InputAdornment, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material'

import { useGetTopGrossingGamesQuery } from '../../../api/top-grossing'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { useCurrentUserLanguage } from '../../../features/account/hooks/useCurrentUserLanguage'
import MarketSelector from '../../../features/markets/components/MarketSelector/MarketSelector'
import { selectPublishedMarkets } from '../../../features/markets/selectors/selectors'
import { useAppSelector } from '../../../hooks/storeHooks'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetDashboardMarketQuery, useGetDashboardSummaryQuery, useGetFtueVideoCountsQuery, useSaveDashboardMarketMutation } from '../../api/dashboard'
import { useDeleteInternalGameMutation, useLazySearchInternalGameListQuery } from '../../api/games'
import { AnalysisListDialog } from '../../components/AnalysisListDialog/AnalysisListDialog'
import { EditGameDialog } from '../../components/EditGameDialog/EditGameDialog'
import { VersionTagsDialog } from '../../components/VersionTagsDialog/VersionTagsDialog'
import { getCreatedAnalysisPath } from '../../features/analysis/helpers/analysisPaths'
import { AnalystFilterSelect } from '../../features/dashboard/components/AnalystFilterSelect/AnalystFilterSelect'
import { DashboardCounter, DashboardCounters } from '../../features/dashboard/components/DashboardCounters/DashboardCounters'
import { DashboardGamesTable } from '../../features/dashboard/components/DashboardGamesTable/DashboardGamesTable'
import { MarketShareGamesDialog } from '../../features/dashboard/components/MarketShareGamesDialog/MarketShareGamesDialog'
import { NotAnalyzedGamesDialog } from '../../features/dashboard/components/NotAnalyzedGamesDialog/NotAnalyzedGamesDialog'
import { NotUpdatedGamesDialog } from '../../features/dashboard/components/NotUpdatedGamesDialog/NotUpdatedGamesDialog'
import { OutdatedGamesDialog } from '../../features/dashboard/components/OutdatedGamesDialog/OutdatedGamesDialog'
import { PinnedGamesDialog } from '../../features/dashboard/components/PinnedGamesDialog/PinnedGamesDialog'
import {
  filterRowsByAnalyst,
  filterRowsByName,
  getInitialMarketIso,
  getTopListHeadingKey,
  topGameFields,
  topGamesLimit,
  toTopGameRow,
} from '../../features/dashboard/helpers/dashboardHelpers'
import { useDashboardAnalysts } from '../../features/dashboard/hooks/useDashboardAnalysts'
import {
  DashboardAction,
  DashboardActionHandler,
  DashboardColumn,
  DashboardGameRow,
  RankType,
  StoredSubgenres,
  allAnalystsId,
} from '../../features/dashboard/types/Dashboard'
import { allMarketsIso, InternalGameDocument } from '../../features/games/types/InternalGame'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

enum DashboardDialog {
  WithoutAnalysis = 'withoutAnalysis',
  Outdated = 'outdated',
  NotUpdated = 'notUpdated',
  MarketShare = 'marketShare',
  Pinned = 'pinned',
}

const counterDialogs: Record<DashboardCounter, DashboardDialog> = {
  [DashboardCounter.WithoutAnalysis]: DashboardDialog.WithoutAnalysis,
  [DashboardCounter.Outdated]: DashboardDialog.Outdated,
  [DashboardCounter.NotUpdated]: DashboardDialog.NotUpdated,
}

const topColumns = [
  DashboardColumn.Analyst,
  DashboardColumn.Rank,
  DashboardColumn.FreeRank,
  DashboardColumn.Icon,
  DashboardColumn.Name,
  DashboardColumn.Publisher,
  DashboardColumn.Subgenre,
  DashboardColumn.Powerscore,
  DashboardColumn.Ftue,
  DashboardColumn.AnalystOverview,
]
const topActions = [DashboardAction.ViewAnalyses, DashboardAction.Edit, DashboardAction.VersionTags, DashboardAction.Delete]
const rankTypes = [
  { value: RankType.Grossing, labelKey: 'internal-analysts:dashboard_rank_grossing' },
  { value: RankType.Free, labelKey: 'internal-analysts:dashboard_rank_free' },
]

type GameDialogState = { open: boolean; gameId?: string }
type VersionTagsDialogState = { open: boolean; appId?: number; gameName: string }

export const DashboardPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-dashboard'))
  const { t } = useTranslation()
  const { run, notify, notifyError, notifySuccess } = useMutationFeedback()
  const navigate = useNavigate()
  const userLanguage = useCurrentUserLanguage()
  const publishedMarketsSelector = useMemo(() => selectPublishedMarkets(userLanguage), [userLanguage])
  const publishedMarkets = useAppSelector(publishedMarketsSelector)
  const publishedIsos = useMemo(() => publishedMarkets.map(({ iso }) => iso), [publishedMarkets])

  const marketQuery = useGetDashboardMarketQuery()
  const [saveMarket] = useSaveDashboardMarketMutation()
  const [pickedIso, setPickedIso] = useState<string>()
  const isMarketSettled = marketQuery.isSuccess || marketQuery.isError
  const marketIso = pickedIso ?? (isMarketSettled && publishedIsos.length ? getInitialMarketIso(marketQuery.data ?? undefined, publishedIsos) : undefined)

  const [openDialog, setOpenDialog] = useState<DashboardDialog>()
  const [rankType, setRankType] = useState(RankType.Grossing)
  const [analystId, setAnalystId] = useState(allAnalystsId)
  const [search, setSearch] = useState('')
  const [storedSubgenres, setStoredSubgenres] = useState<StoredSubgenres>({})
  const [deletedGameIds, setDeletedGameIds] = useState<string[]>([])
  const [gameDialog, setGameDialog] = useState<GameDialogState>({ open: false })
  const [analysisDialog, setAnalysisDialog] = useState<GameDialogState>({ open: false })
  const [versionTagsDialog, setVersionTagsDialog] = useState<VersionTagsDialogState>({ open: false, gameName: '' })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<DashboardGameRow>>()

  const summaryQuery = useGetDashboardSummaryQuery(marketIso ?? '', { skip: !marketIso, refetchOnMountOrArgChange: true })
  const topQuery = useGetTopGrossingGamesQuery(
    {
      marketIso: marketIso ?? '',
      include: topGameFields.join(','),
      limit: topGamesLimit,
      ...(rankType === RankType.Free ? { orderBy: 'drank' as const } : {}),
    },
    { skip: !marketIso, refetchOnMountOrArgChange: true }
  )
  const ftueQuery = useGetFtueVideoCountsQuery(undefined, { refetchOnMountOrArgChange: true })
  const { analystMap, analysts, analystsByAppId, isError: isAnalystsError } = useDashboardAnalysts()
  const [deleteGame] = useDeleteInternalGameMutation()
  const [searchGames] = useLazySearchInternalGameListQuery()

  const rows = useMemo(
    () => (topQuery.data ?? []).map(toTopGameRow).filter(({ game }) => !game.id || !deletedGameIds.includes(game.id)),
    [deletedGameIds, topQuery.data]
  )
  const shownRows = useMemo(() => filterRowsByName(filterRowsByAnalyst(rows, analystMap, analystId), search), [analystId, analystMap, rows, search])

  const handleMarketChange = async (iso: string) => {
    setPickedIso(iso)
    const saved = await run(saveMarket(iso).unwrap(), { error: t('internal-analysts:dashboard_market_save_failed') })
    if (saved.ok && saved.result !== iso) notifyError(t('internal-analysts:dashboard_market_not_stored'))
  }

  const handleAction = useCallback<DashboardActionHandler>(
    (action, row) => {
      const { game } = row
      switch (action) {
        case DashboardAction.ViewAnalyses:
          return setAnalysisDialog({ open: true, gameId: game.id })
        case DashboardAction.Edit:
          return setGameDialog({ open: true, gameId: game.id })
        case DashboardAction.VersionTags:
          return setVersionTagsDialog({ open: true, appId: game.appId, gameName: game.name ?? '' })
        case DashboardAction.Delete:
          return setConfirmDialogData({
            title: t('internal-analysts:games_delete'),
            confirmText: t('internal-analysts:games_delete_confirm', { name: game.name || '---' }),
            actionText: t('internal-common:delete'),
            destructiveAction: true,
            data: row,
          })
      }
    },
    [t]
  )

  // The gateway's 404 has no CORS headers, so a delete is confirmed by no longer finding the game.
  const handleConfirmDelete = async (confirmed?: ConfirmDialogData<DashboardGameRow>) => {
    setConfirmDialogData(undefined)
    const game = confirmed?.data?.game
    if (!game?.id) return
    const name = game.name || '---'
    try {
      await deleteGame(game.id).unwrap()
      const remaining = await searchGames({ term: game.name ?? '', marketIso: allMarketsIso }).unwrap()
      if (remaining.some(({ id }) => id === game.id)) return notifyError(t('internal-analysts:games_not_deleted', { name }))
      setDeletedGameIds((current) => [...current, game.id])
      notifySuccess(t('internal-analysts:games_deleted', { name }))
    } catch (error) {
      const reason = getValidationErrorMessage(error)
      notifyError(reason ? t('internal-analysts:games_delete_failed_reason', { name, reason }) : t('internal-analysts:games_delete_failed', { name }))
    }
  }

  const handleGameSaved = (stored: InternalGameDocument) => {
    const gameId = stored.id
    if (gameId) setStoredSubgenres((current) => ({ ...current, [gameId]: stored.conventionalSubgenre ?? null }))
  }

  const closeDialog = () => setOpenDialog(undefined)
  const listDialogProps = { marketIso: marketIso ?? '', onClose: closeDialog, onAction: handleAction, storedSubgenres }

  const renderTopList = () => {
    if (!marketIso || (topQuery.isFetching && !topQuery.data)) return <Skeleton variant="rectangular" height={480} />
    if (topQuery.isError) return <Typography color="error.main">{t('internal-analysts:dashboard_top_failed')}</Typography>
    return (
      <DashboardGamesTable
        rows={shownRows}
        columns={topColumns}
        actions={topActions}
        onAction={handleAction}
        analystsByAppId={analystsByAppId}
        ftueCounts={ftueQuery.data}
        storedSubgenres={storedSubgenres}
        isLoading={topQuery.isFetching}
        noRowsLabel={t('internal-analysts:dashboard_no_games')}
      />
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} spacing={2}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" size="small" startIcon={<Category />} onClick={() => setOpenDialog(DashboardDialog.MarketShare)}>
              {t('internal-analysts:dashboard_market_share_open')}
            </Button>
            <Button variant="contained" size="small" startIcon={<PushPin />} onClick={() => setOpenDialog(DashboardDialog.Pinned)}>
              {t('internal-analysts:dashboard_pinned_open')}
            </Button>
          </Stack>
        </Stack>
        <Stack alignItems="flex-end" spacing={0.5}>
          {marketIso ? <MarketSelector value={marketIso} onChange={handleMarketChange} /> : <Skeleton variant="rectangular" width={120} height={40} />}
          {marketQuery.isError && (
            <Typography variant="body2" color="error.main">
              {t('internal-analysts:dashboard_market_load_failed')}
            </Typography>
          )}
        </Stack>
      </Stack>

      <Box mb={3}>
        <DashboardCounters
          summary={summaryQuery.data}
          isLoading={!marketIso || summaryQuery.isFetching}
          isError={summaryQuery.isError}
          onOpen={(counter) => setOpenDialog(counterDialogs[counter])}
        />
      </Box>

      <Typography variant="h5" fontWeight="bold" mb={2}>
        {t(getTopListHeadingKey(rows.length))}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <AnalystFilterSelect analysts={analysts} value={analystId} onChange={setAnalystId} />
        <TextField
          size="small"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('internal-analysts:dashboard_search_games')}
          inputProps={{ 'aria-label': t('internal-analysts:dashboard_search_games') }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <TextField
          select
          size="small"
          label={t('internal-analysts:dashboard_rank_type')}
          value={rankType}
          onChange={(event) => setRankType(event.target.value as RankType)}
          sx={{ minWidth: 180 }}
        >
          {rankTypes.map((type) => (
            <MenuItem key={type.value} value={type.value}>
              {t(type.labelKey)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      {(isAnalystsError || ftueQuery.isError) && (
        <Typography color="error.main" mb={2}>
          {t(isAnalystsError ? 'internal-analysts:dashboard_analysts_failed' : 'internal-analysts:dashboard_ftue_failed')}
        </Typography>
      )}
      {renderTopList()}

      {marketIso && (
        <>
          <NotAnalyzedGamesDialog open={openDialog === DashboardDialog.WithoutAnalysis} {...listDialogProps} />
          <OutdatedGamesDialog open={openDialog === DashboardDialog.Outdated} {...listDialogProps} />
          <NotUpdatedGamesDialog open={openDialog === DashboardDialog.NotUpdated} {...listDialogProps} />
          <PinnedGamesDialog open={openDialog === DashboardDialog.Pinned} {...listDialogProps} onMessage={notify} />
        </>
      )}
      <MarketShareGamesDialog
        open={openDialog === DashboardDialog.MarketShare}
        onClose={closeDialog}
        onAction={handleAction}
        storedSubgenres={storedSubgenres}
      />

      <EditGameDialog open={gameDialog.open} gameId={gameDialog.gameId} onClose={() => setGameDialog({ open: false })} onSaved={handleGameSaved} />
      <AnalysisListDialog
        open={analysisDialog.open}
        gameId={analysisDialog.gameId}
        onClose={() => setAnalysisDialog({ open: false })}
        onAnalysisCreated={(id, comparedMarketIso) => analysisDialog.gameId && navigate(getCreatedAnalysisPath(id, analysisDialog.gameId, comparedMarketIso))}
      />
      <VersionTagsDialog
        open={versionTagsDialog.open}
        appId={versionTagsDialog.appId}
        gameName={versionTagsDialog.gameName}
        marketIso={marketIso ?? ''}
        onClose={() => setVersionTagsDialog({ open: false, gameName: '' })}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
